import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/PayrollChanges.css';
import { useUser } from '../../contexts/UserContext';
import api from '../../services/api';
import SearchableSelect from '../shared/SearchableSelect';

const PayrollChanges = ({ isEditingAllowed, selectedMonth, selectedYear, onTabChange }) => {
    const [changes, setChanges] = useState([]);
    const [changeType, setChangeType] = useState('variable');
    const [showNewRow, setShowNewRow] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [payrollTypes, setPayrollTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [showDownloadOptions, setShowDownloadOptions] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    
    // Nye state variabler til at holde styr på ændringer
    const [pendingChanges, setPendingChanges] = useState({
        create: [],
        update: [],
        delete: []
    });
    const [originalData, setOriginalData] = useState({});  // Gemmer original data for at kunne fortryde
    const [isSaving, setIsSaving] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentingId, setCommentingId] = useState(null);
    const [expandedCommentRows, setExpandedCommentRows] = useState(new Set());
    const [commentPopupPosition, setCommentPopupPosition] = useState({ top: 0, left: 0 });
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [pendingNavigationAction, setPendingNavigationAction] = useState(null);

    const { user } = useUser();
    // Giv midlertidigt kunder samme rettigheder som admin/konsulent
    const canProcessChanges = user.role === 'admin' || user.role === 'consultant';
    const canEditChanges = user.role === 'admin' || user.role === 'consultant' || user.role === 'client';
    const canDownload = user.role === 'admin' || user.role === 'consultant'; // Kun admin og konsulent kan downloade

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Hent resten af data
                try {
                    const [employeesRes, payrollTypesRes, changesRes] = await Promise.all([
                        api.get('/payroll/employees'),
                        api.get('/payroll/types'),
                        api.get('/payroll/inputs')
                    ]);

                    setEmployees(employeesRes);
                    setPayrollTypes(payrollTypesRes);
                    setChanges(changesRes);
                } catch (err) {
                    setError('Der opstod en fejl ved indlæsning af data');
                    console.error('Fejl ved indlæsning:', err);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedMonth, selectedYear, user.clientId, user.role]); // Genindlæs data når måned/år ændres

    // Gem original data når komponenten indlæses
    useEffect(() => {
        const dataMap = {};
        changes.forEach(change => {
            dataMap[change.id] = { ...change };
        });
        setOriginalData(dataMap);
    }, [changes]);

    const [newEntry, setNewEntry] = useState({
        type: 'variable',
        employee_number: '',
        payroll_code: '',
        rate: '',
        units: '',
        amount: '',
        alternative_text: '',
        description: '',
        comment: '',
        valid_from: '',
        status: 'pending',
        period_month: selectedMonth,
        period_year: selectedYear
    });

    const handleAddNew = async () => {
        try {
            setError(null);
            const payload = {
                ...newEntry,
                type: changeType,
                rate: newEntry.rate ? parseFloat(newEntry.rate) : null,
                units: newEntry.units ? parseFloat(newEntry.units) : null,
                amount: newEntry.amount ? parseFloat(newEntry.amount) : 0,
                alternative_text: newEntry.alternative_text || '',
                description: newEntry.description || '',
                comment: newEntry.comment || '',
                employee_number: newEntry.employee_number || null,
                payroll_code: newEntry.payroll_code || null,
                period_month: selectedMonth,
                period_year: selectedYear,
                valid_from: newEntry.valid_from ? new Date(newEntry.valid_from).toISOString() : null
            };

            // Find medarbejder og lønart information
            const employee = employees.find(e => e.employee_number === newEntry.employee_number);
            const payroll_type = payrollTypes.find(pt => pt.code === newEntry.payroll_code);

            if (newEntry.id) {
                // Find den eksisterende ændring
                const existingChange = changes.find(c => c.id === newEntry.id);
                const newStatus = existingChange?.status === 'processed' ? 'modified' : existingChange?.status || 'pending';

                // Opdater lokalt og gem i pending updates
                const updatedChange = {
                    id: newEntry.id,
                    ...payload,
                    status: newStatus,
                    employee,
                    payroll_type
                };
                setPendingChanges(prev => ({
                    ...prev,
                    update: [...prev.update, updatedChange]
                }));
                setChanges(prevChanges => 
                    prevChanges.map(c => c.id === newEntry.id ? updatedChange : c)
                );
            } else {
                // Tilføj lokalt med temporary ID og gem i pending creates
                const tempId = `temp_${Date.now()}`;
                const newChange = {
                    id: tempId,
                    ...payload,
                    status: 'pending',
                    employee,
                    payroll_type
                };
                setPendingChanges(prev => ({
                    ...prev,
                    create: [...prev.create, newChange]
                }));
                setChanges(prevChanges => [...prevChanges, newChange]);
            }
            
            // Nulstil formular
            setNewEntry({
                type: changeType,
                employee_number: '',
                payroll_code: '',
                rate: '',
                units: '',
                amount: '',
                alternative_text: '',
                description: '',
                comment: '',
                valid_from: '',
                status: 'pending',
                period_month: selectedMonth,
                period_year: selectedYear
            });
            setShowNewRow(false);
        } catch (err) {
            setError('Der opstod en fejl ved oprettelse/redigering af ændringen');
            console.error('Fejl ved oprettelse/redigering:', err);
        }
    };

    const handleCancel = () => {
        setShowNewRow(false);
        setNewEntry({
            type: changeType,
            employee_number: '',
            payroll_code: '',
            rate: '',
            units: '',
            amount: '',
            alternative_text: '',
            description: '',
            comment: '',
            valid_from: '',
            status: 'pending',
            period_month: selectedMonth,
            period_year: selectedYear
        });
    };

    const handleMarkAsProcessed = async (id) => {
        try {
            setError(null);
            
            // Find den eksisterende ændring
            const existingChange = changes.find(c => c.id === id);
            if (!existingChange) {
                throw new Error('Kunne ikke finde ændringen');
            }

            // Opdater UI med den nye status med det samme
            const updatedChange = {
                ...existingChange,
                status: 'processed'
            };

            // Opdater state øjeblikkeligt
            setChanges(prevChanges => 
                prevChanges.map(change => 
                    change.id === id ? updatedChange : change
                )
            );

            // Send PATCH request i baggrunden
            api.patch(`/payroll/inputs/${id}`, {
                status: 'processed'
            }).catch(err => {
                console.error('Fejl ved markering som behandlet:', err);
                // Hvis PATCH fejler, nulstil UI tilbage til pending
                setChanges(prevChanges => 
                    prevChanges.map(change => 
                        change.id === id ? existingChange : change
                    )
                );
                setError(err.message || 'Der opstod en fejl ved markering som behandlet');
            });

        } catch (err) {
            console.error('Fejl ved markering som behandlet:', err);
            setError(err.message || 'Der opstod en fejl ved markering som behandlet');
        }
    };

    const handleDelete = async (id) => {
        try {
            setError(null);
            setDeletingId(id);
            
            // Gem i pending deletes og fjern fra UI
            setPendingChanges(prev => ({
                ...prev,
                delete: [...prev.delete, id]
            }));
            setChanges(prevChanges => prevChanges.filter(c => c.id !== id));
        } catch (err) {
            setError('Der opstod en fejl ved sletning af ændringen');
            console.error('Fejl ved sletning:', err);
        } finally {
            setDeletingId(null);
        }
    };

    const handleSaveChanges = async () => {
        try {
            setIsSaving(true);
            setError(null);

            // Håndter creates
            for (const newChange of pendingChanges.create) {
                const { id, employee, payroll_type, ...payload } = newChange;
                const response = await api.post('/payroll/inputs', payload);
                const updatedChange = {
                    ...response,
                    employee: employees.find(e => e.employee_number === response.employee_number),
                    payroll_type: payrollTypes.find(pt => pt.code === response.payroll_code)
                };
                setChanges(prev => prev.map(c => c.id === id ? updatedChange : c));
            }

            // Håndter updates
            for (const update of pendingChanges.update) {
                const { id, employee, payroll_type, ...payload } = update;
                // Bevar 'modified' status hvis den eksisterer
                const existingChange = changes.find(c => c.id === id);
                if (existingChange?.status === 'modified') {
                    payload.status = 'modified';
                }
                const response = await api.put(`/payroll/inputs/${id}`, payload);
                const updatedChange = {
                    ...response,
                    employee: employees.find(e => e.employee_number === response.employee_number),
                    payroll_type: payrollTypes.find(pt => pt.code === response.payroll_code)
                };
                setChanges(prev => prev.map(c => c.id === id ? updatedChange : c));
            }

            // Håndter deletes
            for (const id of pendingChanges.delete) {
                await api.delete(`/payroll/inputs/${id}`);
                setChanges(prev => prev.filter(c => c.id !== id));
            }

            // Nulstil pending changes
            setPendingChanges({
                create: [],
                update: [],
                delete: []
            });

        } catch (err) {
            console.error('Fejl ved gem:', err);
            setError(err.message || 'Der opstod en fejl ved gem af ændringer');
        } finally {
            setIsSaving(false);
        }
    };

    const hasPendingChanges = 
        pendingChanges.create.length > 0 || 
        pendingChanges.update.length > 0 || 
        pendingChanges.delete.length > 0;

    const filteredChanges = changes.filter(change => 
        change.type === changeType && 
        change.period_month === selectedMonth && 
        change.period_year === selectedYear
    );

    const handleUndoChange = (id) => {
        // Find hvilken type ændring det er
        if (pendingChanges.create.find(c => c.id === id)) {
            setPendingChanges(prev => ({
                ...prev,
                create: prev.create.filter(c => c.id !== id)
            }));
            setChanges(prev => prev.filter(c => c.id !== id));
        } else if (pendingChanges.update.find(c => c.id === id)) {
            setPendingChanges(prev => ({
                ...prev,
                update: prev.update.filter(c => c.id !== id)
            }));
            setChanges(prev => prev.map(c => 
                c.id === id ? originalData[id] : c
            ));
        } else if (pendingChanges.delete.includes(id)) {
            setPendingChanges(prev => ({
                ...prev,
                delete: prev.delete.filter(deleteId => deleteId !== id)
            }));
            setChanges(prev => [...prev, originalData[id]]);
        }
    };

    const isChangeModified = (change) => {
        if (pendingChanges.create.find(c => c.id === change.id)) return 'new';
        if (pendingChanges.update.find(c => c.id === change.id)) return 'modified';
        if (pendingChanges.delete.includes(change.id)) return 'deleted';
        return null;
    };

    const handleSaveClick = async () => {
        await handleSaveChanges();
    };

    // Tilføj formateringsfunktioner
    const formatNumber = (number) => {
        if (number === null || number === undefined) return '';
        return new Intl.NumberFormat('da-DK', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(number);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            // Opret en dato i dansk tidszone (GMT+2)
            const date = new Date(dateString);
            // Tilføj 2 timer for at kompensere for GMT+2
            date.setHours(date.getHours() + 2);
            
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        } catch (error) {
            console.error('Fejl ved formatering af dato:', error);
            return dateString;
        }
    };

    const handleDownload = async (type) => {
        try {
            setError(null);
            setIsDownloading(true);
            setShowDownloadOptions(false);
            
            const blob = await api.downloadExcel('/payroll/inputs/download', { 
                type,
                period_month: selectedMonth,
                period_year: selectedYear
            });
            
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `lønlinjer_${type === 'all' ? 'alle' : 'ubehandlede'}_${selectedMonth}_${selectedYear}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('Fejl ved download af lønlinjer:', err);
            setError('Der opstod en fejl ved download af lønlinjer');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleAddComment = async (id) => {
        try {
            setError(null);
            const response = await api.post(`/payroll/inputs/${id}/comments`, {
                comment: newComment
            });

            // Opdater lokalt state med den nye kommentar øverst
            setChanges(prevChanges => 
                prevChanges.map(change => 
                    change.id === id 
                        ? { 
                            ...change, 
                            comments: [response, ...(change.comments || [])] // Tilføj ny kommentar først i array'et
                        }
                        : change
                )
            );

            // Nulstil formular
            setNewComment('');
            setCommentingId(null);
        } catch (err) {
            setError('Der opstod en fejl ved tilføjelse af kommentar');
            console.error('Fejl ved tilføjelse af kommentar:', err);
        }
    };

    const formatCommentDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('da-DK', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            console.error('Fejl ved formatering af kommentardato:', error);
            return dateString;
        }
    };

    const getCommentAuthor = (comment) => {
        if (comment.created_by.role === 'client') {
            return 'Kunde';
        } else if (comment.created_by.role === 'admin' || comment.created_by.role === 'consultant') {
            return 'Lønpartner';
        }
        return `${comment.created_by.first_name} ${comment.created_by.last_name}`;
    };

    const toggleCommentExpansion = (id) => {
        const newExpandedRows = new Set(expandedCommentRows);
        if (newExpandedRows.has(id)) {
            newExpandedRows.delete(id);
        } else {
            newExpandedRows.add(id);
        }
        setExpandedCommentRows(newExpandedRows);
    };

    const handleCommentButtonClick = (event, changeId) => {
        event.stopPropagation();
        const buttonRect = event.currentTarget.getBoundingClientRect();
        const popupWidth = 320; // Bredden af popup'en fra CSS
        
        // Beregn position så popup'en er synlig på skærmen
        const left = Math.min(
            buttonRect.right - popupWidth,
            window.innerWidth - popupWidth - 20
        );
        
        const top = buttonRect.top + window.scrollY;
        
        setCommentPopupPosition({ top, left });
        setCommentingId(changeId);
    };

    const hasUnsavedChanges = useCallback(() => {
        return showNewRow || hasPendingChanges;
    }, [showNewRow, hasPendingChanges]);

    // Håndter skift af fane med prompt
    const handleTypeChange = (newType) => {
        if (hasUnsavedChanges()) {
            setPendingNavigationAction(() => () => {
                setChangeType(newType);
                // Nulstil formular hvis der var en ny række i gang
                if (showNewRow) {
                    handleCancel();
                }
            });
            setShowUnsavedChangesModal(true);
        } else {
            setChangeType(newType);
        }
    };

    // Håndter bekræftelse af navigation
    const handleConfirmNavigation = () => {
        if (pendingNavigationAction) {
            pendingNavigationAction();
        }
        setShowUnsavedChangesModal(false);
        setPendingNavigationAction(null);
    };

    // Håndter afvisning af navigation
    const handleCancelNavigation = () => {
        setShowUnsavedChangesModal(false);
        setPendingNavigationAction(null);
    };

    // Tilføj event listener for browser navigation og tab skift
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        const handleTabChange = () => {
            if (hasUnsavedChanges()) {
                return false;
            }
            return true;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // Registrer callback for tab skift
        if (onTabChange) {
            onTabChange(handleTabChange);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [showNewRow, hasPendingChanges, onTabChange, hasUnsavedChanges]);

    if (isLoading) {
        return <div className="loading">Indlæser...</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="payroll-changes">
            <div className="toggle-container">
                <button 
                    className={`toggle-button ${changeType === 'variable' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('variable')}
                >
                    Løndele
                </button>
                <button 
                    className={`toggle-button ${changeType === 'employee' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('employee')}
                >
                    Medarbejder
                </button>
                <button 
                    className={`toggle-button ${changeType === 'general' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('general')}
                >
                    Generel
                </button>
            </div>

            {/* Tilføj modal for ikke-gemte ændringer */}
            {showUnsavedChangesModal && (
                <div className="unsaved-changes-modal">
                    <div className="modal-content">
                        <p>Du har ikke-gemte ændringer. Vil du fortsætte?</p>
                        <div className="modal-actions">
                            <button className="secondary-btn" onClick={handleCancelNavigation}>Bliv på siden</button>
                            <button className="primary-btn" onClick={handleConfirmNavigation}>Fortsæt</button>
                        </div>
                    </div>
                </div>
            )}

            {isEditingAllowed && (
                <div className="table-header">
                    <div className="right-buttons">
                        {changeType === 'variable' && canDownload && (
                            <div className="download-dropdown">
                                <button 
                                    className="download-button"
                                    onClick={() => setShowDownloadOptions(prev => !prev)}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Downloader...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-download"></i>
                                            Download
                                        </>
                                    )}
                                </button>
                                {showDownloadOptions && !isDownloading && (
                                    <div className="download-options">
                                        <button onClick={() => handleDownload('all')}>
                                            Download alle linjer
                                        </button>
                                        <button onClick={() => handleDownload('pending')}>
                                            Download ubehandlede linjer
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button 
                            className="add-new-button"
                            onClick={() => setShowNewRow(true)}
                        >
                            Tilføj ny ændring
                        </button>
                        {hasPendingChanges && (
                            <button 
                                className="save-changes-button"
                                onClick={handleSaveClick}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Gemmer ændringer...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        Gem ændringer ({pendingChanges.create.length + pendingChanges.update.length + pendingChanges.delete.length})
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <table className={`payroll-table type-${changeType}`}>
                <thead>
                    <tr>
                        {changeType === 'variable' ? (
                            <>
                                <th>Medarbejder</th>
                                <th>Lønart</th>
                                <th>Enheder</th>
                                <th>Sats</th>
                                <th>Beløb</th>
                                <th>Alternativ tekst til lønseddel</th>
                                <th>Kommentarer</th>
                                <th>Status</th>
                            </>
                        ) : changeType === 'employee' ? (
                            <>
                                <th>Medarbejder</th>
                                <th>Beskrivelse af ændring</th>
                                <th>Beløb</th>
                                <th>Gældende fra</th>
                                <th>Kommentarer</th>
                                <th>Status</th>
                            </>
                        ) : (
                            <>
                                <th>Beskrivelse af ændring</th>
                                <th>Beløb</th>
                                <th>Gældende fra</th>
                                <th>Kommentarer</th>
                                <th>Status</th>
                            </>
                        )}
                        {isEditingAllowed && <th>Handlinger</th>}
                    </tr>
                </thead>
                <tbody>
                    {showNewRow && (
                        <tr className="new-row">
                            {changeType === 'variable' ? (
                                <>
                                    <td>
                                        <SearchableSelect
                                            options={employees.map(emp => ({
                                                id: emp.employee_number,
                                                name: `${emp.first_name} ${emp.last_name} (${emp.employee_number})`
                                            }))}
                                            value={newEntry.employee_number}
                                            onChange={value => {
                                                const selectedEmployee = employees.find(emp => emp.employee_number === value);
                                                setNewEntry({
                                                    ...newEntry, 
                                                    employee_number: value,
                                                    employee: selectedEmployee
                                                });
                                            }}
                                            placeholder="Vælg medarbejder"
                                        />
                                    </td>
                                    <td>
                                        <SearchableSelect
                                            options={payrollTypes.map(pt => ({
                                                id: pt.code,
                                                name: pt.zenegy_code ? pt.name : `${pt.name} (${pt.code})`
                                            }))}
                                            value={newEntry.payroll_code}
                                            onChange={value => {
                                                const selectedPayrollType = payrollTypes.find(pt => pt.code === value);
                                                setNewEntry(prev => ({
                                                    ...prev, 
                                                    payroll_code: value,
                                                    payroll_type: selectedPayrollType
                                                }));
                                            }}
                                            placeholder="Vælg lønart"
                                        />
                                    </td>
                                    <td><input
                                        type="number"
                                        placeholder="Enheder"
                                        value={newEntry.units}
                                        onChange={e => setNewEntry({...newEntry, units: e.target.value})}
                                        step="0.01"
                                    /></td>
                                    <td><input
                                        type="number"
                                        placeholder="Sats"
                                        value={newEntry.rate}
                                        onChange={e => setNewEntry({...newEntry, rate: e.target.value})}
                                        step="0.01"
                                    /></td>
                                    <td><input
                                        type="number"
                                        placeholder="Beløb"
                                        value={newEntry.amount}
                                        onChange={e => setNewEntry({...newEntry, amount: e.target.value})}
                                        step="0.01"
                                    /></td>
                                    <td><input
                                        placeholder="Alternativ tekst"
                                        value={newEntry.alternative_text}
                                        onChange={e => setNewEntry({...newEntry, alternative_text: e.target.value})}
                                    /></td>
                                    <td>
                                        <textarea
                                            placeholder="Tilføj kommentar"
                                            value={newEntry.comment}
                                            onChange={e => setNewEntry({...newEntry, comment: e.target.value})}
                                            rows="2"
                                        />
                                    </td>
                                    <td>Afventer</td>
                                </>
                            ) : changeType === 'employee' ? (
                                <>
                                    <td>
                                        <SearchableSelect
                                            options={employees.map(emp => ({
                                                id: emp.employee_number,
                                                name: `${emp.first_name} ${emp.last_name} (${emp.employee_number})`
                                            }))}
                                            value={newEntry.employee_number}
                                            onChange={value => {
                                                const selectedEmployee = employees.find(emp => emp.employee_number === value);
                                                setNewEntry(prev => ({
                                                    ...prev, 
                                                    employee_number: value,
                                                    employee: selectedEmployee
                                                }));
                                            }}
                                            placeholder="Vælg medarbejder"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            placeholder="Beskrivelse af ændring"
                                            value={newEntry.description}
                                            onChange={e => setNewEntry({...newEntry, description: e.target.value})}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            placeholder="Beløb"
                                            value={newEntry.amount}
                                            onChange={e => setNewEntry({...newEntry, amount: e.target.value})}
                                            step="0.01"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="date"
                                            value={newEntry.valid_from}
                                            onChange={e => setNewEntry({...newEntry, valid_from: e.target.value})}
                                        />
                                    </td>
                                    <td>
                                        <textarea
                                            placeholder="Tilføj kommentar"
                                            value={newEntry.comment}
                                            onChange={e => setNewEntry({...newEntry, comment: e.target.value})}
                                            rows="2"
                                        />
                                    </td>
                                    <td>Afventer</td>
                                </>
                            ) : (
                                <>
                                    <td><input
                                        placeholder="Beskrivelse af ændring"
                                        value={newEntry.description}
                                        onChange={e => setNewEntry({...newEntry, description: e.target.value})}
                                    /></td>
                                    <td><input
                                        type="number"
                                        placeholder="Beløb"
                                        value={newEntry.amount}
                                        onChange={e => setNewEntry({...newEntry, amount: e.target.value})}
                                        step="0.01"
                                    /></td>
                                    <td><input
                                        type="date"
                                        placeholder="Gældende fra"
                                        value={newEntry.valid_from}
                                        onChange={e => setNewEntry({...newEntry, valid_from: e.target.value})}
                                    /></td>
                                    <td>
                                        <textarea
                                            placeholder="Tilføj kommentar"
                                            value={newEntry.comment}
                                            onChange={e => setNewEntry({...newEntry, comment: e.target.value})}
                                            rows="2"
                                        />
                                    </td>
                                    <td>Afventer</td>
                                </>
                            )}
                            <td>
                                <button onClick={handleAddNew}>Gem</button>
                                <button onClick={handleCancel}>Annuller</button>
                            </td>
                        </tr>
                    )}
                    {filteredChanges.map(change => {
                        const modificationState = isChangeModified(change);
                        return (
                            <tr 
                                key={change.id} 
                                className={`${modificationState ? `row-${modificationState}` : ''} ${change.status === 'processed' ? 'row-processed' : ''}`}
                            >
                                {changeType === 'variable' ? (
                                    <>
                                        <td>{change.employee ? `${change.employee.first_name} ${change.employee.last_name}` : ''}</td>
                                        <td>{change.payroll_type ? change.payroll_type.name : ''}</td>
                                        <td>{formatNumber(change.units)}</td>
                                        <td>{formatNumber(change.rate)}</td>
                                        <td>{formatNumber(change.amount)}</td>
                                        <td>{change.alternative_text}</td>
                                        <td className="comments-cell">
                                            <div className="comments-list">
                                                {change.comments && change.comments.length > 0 ? (
                                                    <>
                                                        <div className="comment-item latest-comment">
                                                            <div className="comment-header">
                                                                <span className="comment-author">{getCommentAuthor(change.comments[0])}</span>
                                                                <span className="comment-date">{formatCommentDate(change.comments[0].created_at)}</span>
                                                            </div>
                                                            <div className="comment-text">{change.comments[0].comment}</div>
                                                            <div className="comments-actions">
                                                                {change.comments.length > 1 && (
                                                                    <span className="comments-count" onClick={() => toggleCommentExpansion(change.id)}>
                                                                        ({change.comments.length})
                                                                        <i className={`fas fa-chevron-${expandedCommentRows.has(change.id) ? 'up' : 'down'}`} />
                                                                    </span>
                                                                )}
                                                                <button 
                                                                    className="add-comment-button"
                                                                    onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                    title="Tilføj kommentar"
                                                                >
                                                                    <i className="fas fa-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {expandedCommentRows.has(change.id) && change.comments.slice(1).map(comment => (
                                                            <div key={comment.id} className="comment-item">
                                                                <div className="comment-header">
                                                                    <span className="comment-author">{getCommentAuthor(comment)}</span>
                                                                    <span className="comment-date">{formatCommentDate(comment.created_at)}</span>
                                                                </div>
                                                                <div className="comment-text">{comment.comment}</div>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="comment-item">
                                                        <div className="comments-actions">
                                                            <button 
                                                                className="add-comment-button"
                                                                onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                title="Tilføj kommentar"
                                                            >
                                                                <i className="fas fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {commentingId === change.id && (
                                                <>
                                                    <div className="add-comment-backdrop" onClick={() => {
                                                        setNewComment('');
                                                        setCommentingId(null);
                                                    }} />
                                                    <div 
                                                        className="add-comment active"
                                                        style={{
                                                            top: `${commentPopupPosition.top}px`,
                                                            left: `${commentPopupPosition.left}px`
                                                        }}
                                                    >
                                                        <textarea
                                                            placeholder="Tilføj kommentar"
                                                            value={newComment}
                                                            onChange={e => setNewComment(e.target.value)}
                                                            rows="4"
                                                        />
                                                        <div className="comment-actions">
                                                            <button onClick={() => handleAddComment(change.id)}>Tilføj</button>
                                                            <button onClick={() => {
                                                                setNewComment('');
                                                                setCommentingId(null);
                                                            }}>Annuller</button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </td>
                                        <td className={`status-${change.status}`}>
                                            {change.status === 'pending' ? 'Afventer' : 
                                             change.status === 'processed' ? 'Behandlet' : 
                                             change.status === 'modified' ? 'Ændret' : change.status}
                                        </td>
                                    </>
                                ) : changeType === 'employee' ? (
                                    <>
                                        <td>{change.employee ? `${change.employee.first_name} ${change.employee.last_name}` : ''}</td>
                                        <td>{change.description}</td>
                                        <td>{formatNumber(change.amount)}</td>
                                        <td>{formatDate(change.valid_from)}</td>
                                        <td className="comments-cell">
                                            <div className="comments-list">
                                                {change.comments && change.comments.length > 0 ? (
                                                    <>
                                                        <div className="comment-item latest-comment">
                                                            <div className="comment-header">
                                                                <span className="comment-author">{getCommentAuthor(change.comments[0])}</span>
                                                                <span className="comment-date">{formatCommentDate(change.comments[0].created_at)}</span>
                                                            </div>
                                                            <div className="comment-text">{change.comments[0].comment}</div>
                                                            <div className="comments-actions">
                                                                {change.comments.length > 1 && (
                                                                    <span className="comments-count" onClick={() => toggleCommentExpansion(change.id)}>
                                                                        ({change.comments.length})
                                                                        <i className={`fas fa-chevron-${expandedCommentRows.has(change.id) ? 'up' : 'down'}`} />
                                                                    </span>
                                                                )}
                                                                <button 
                                                                    className="add-comment-button"
                                                                    onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                    title="Tilføj kommentar"
                                                                >
                                                                    <i className="fas fa-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {expandedCommentRows.has(change.id) && change.comments.slice(1).map(comment => (
                                                            <div key={comment.id} className="comment-item">
                                                                <div className="comment-header">
                                                                    <span className="comment-author">{getCommentAuthor(comment)}</span>
                                                                    <span className="comment-date">{formatCommentDate(comment.created_at)}</span>
                                                                </div>
                                                                <div className="comment-text">{comment.comment}</div>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="comment-item">
                                                        <div className="comments-actions">
                                                            <button 
                                                                className="add-comment-button"
                                                                onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                title="Tilføj kommentar"
                                                            >
                                                                <i className="fas fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {commentingId === change.id && (
                                                <>
                                                    <div className="add-comment-backdrop" onClick={() => {
                                                        setNewComment('');
                                                        setCommentingId(null);
                                                    }} />
                                                    <div 
                                                        className="add-comment active"
                                                        style={{
                                                            top: `${commentPopupPosition.top}px`,
                                                            left: `${commentPopupPosition.left}px`
                                                        }}
                                                    >
                                                        <textarea
                                                            placeholder="Tilføj kommentar"
                                                            value={newComment}
                                                            onChange={e => setNewComment(e.target.value)}
                                                            rows="4"
                                                        />
                                                        <div className="comment-actions">
                                                            <button onClick={() => handleAddComment(change.id)}>Tilføj</button>
                                                            <button onClick={() => {
                                                                setNewComment('');
                                                                setCommentingId(null);
                                                            }}>Annuller</button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </td>
                                        <td className={`status-${change.status}`}>
                                            {change.status === 'pending' ? 'Afventer' : 
                                             change.status === 'processed' ? 'Behandlet' : 
                                             change.status === 'modified' ? 'Ændret' : change.status}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{change.description}</td>
                                        <td>{formatNumber(change.amount)}</td>
                                        <td>{formatDate(change.valid_from)}</td>
                                        <td className="comments-cell">
                                            <div className="comments-list">
                                                {change.comments && change.comments.length > 0 ? (
                                                    <>
                                                        <div className="comment-item latest-comment">
                                                            <div className="comment-header">
                                                                <span className="comment-author">{getCommentAuthor(change.comments[0])}</span>
                                                                <span className="comment-date">{formatCommentDate(change.comments[0].created_at)}</span>
                                                            </div>
                                                            <div className="comment-text">{change.comments[0].comment}</div>
                                                            <div className="comments-actions">
                                                                {change.comments.length > 1 && (
                                                                    <span className="comments-count" onClick={() => toggleCommentExpansion(change.id)}>
                                                                        ({change.comments.length})
                                                                        <i className={`fas fa-chevron-${expandedCommentRows.has(change.id) ? 'up' : 'down'}`} />
                                                                    </span>
                                                                )}
                                                                <button 
                                                                    className="add-comment-button"
                                                                    onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                    title="Tilføj kommentar"
                                                                >
                                                                    <i className="fas fa-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {expandedCommentRows.has(change.id) && change.comments.slice(1).map(comment => (
                                                            <div key={comment.id} className="comment-item">
                                                                <div className="comment-header">
                                                                    <span className="comment-author">{getCommentAuthor(comment)}</span>
                                                                    <span className="comment-date">{formatCommentDate(comment.created_at)}</span>
                                                                </div>
                                                                <div className="comment-text">{comment.comment}</div>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="comment-item">
                                                        <div className="comments-actions">
                                                            <button 
                                                                className="add-comment-button"
                                                                onClick={(e) => handleCommentButtonClick(e, change.id)}
                                                                title="Tilføj kommentar"
                                                            >
                                                                <i className="fas fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {commentingId === change.id && (
                                                <>
                                                    <div className="add-comment-backdrop" onClick={() => {
                                                        setNewComment('');
                                                        setCommentingId(null);
                                                    }} />
                                                    <div 
                                                        className="add-comment active"
                                                        style={{
                                                            top: `${commentPopupPosition.top}px`,
                                                            left: `${commentPopupPosition.left}px`
                                                        }}
                                                    >
                                                        <textarea
                                                            placeholder="Tilføj kommentar"
                                                            value={newComment}
                                                            onChange={e => setNewComment(e.target.value)}
                                                            rows="4"
                                                        />
                                                        <div className="comment-actions">
                                                            <button onClick={() => handleAddComment(change.id)}>Tilføj</button>
                                                            <button onClick={() => {
                                                                setNewComment('');
                                                                setCommentingId(null);
                                                            }}>Annuller</button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </td>
                                        <td className={`status-${change.status}`}>
                                            {change.status === 'pending' ? 'Afventer' : 
                                             change.status === 'processed' ? 'Behandlet' : 
                                             change.status === 'modified' ? 'Ændret' : change.status}
                                        </td>
                                    </>
                                )}
                                {isEditingAllowed && (
                                    <td className="action-buttons">
                                        {modificationState && (
                                            <button 
                                                className="undo-btn"
                                                onClick={() => handleUndoChange(change.id)}
                                                title="Fortryd ændring"
                                            >
                                                <i className="fas fa-undo"></i>
                                            </button>
                                        )}
                                        {canEditChanges && (
                                            <button 
                                                className="action-btn edit-btn"
                                                onClick={() => {
                                                    const date = change.valid_from ? new Date(change.valid_from) : null;
                                                    if (date) {
                                                        // Tilføj 2 timer for at kompensere for GMT+2
                                                        date.setHours(date.getHours() + 2);
                                                    }
                                                    setNewEntry({
                                                        id: change.id,
                                                        type: change.type,
                                                        employee_number: change.employee?.employee_number || '',
                                                        payroll_code: change.payroll_type?.code || '',
                                                        rate: change.rate || '',
                                                        units: change.units || '',
                                                        amount: change.amount || '',
                                                        alternative_text: change.alternative_text || '',
                                                        description: change.description || '',
                                                        comment: change.comment || '',
                                                        valid_from: date ? date.toISOString().split('T')[0] : '',
                                                        status: change.status,
                                                        period_month: change.period_month,
                                                        period_year: change.period_year
                                                    });
                                                    setShowNewRow(true);
                                                }}
                                                title="Rediger"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                        )}
                                        {canProcessChanges && (change.status === 'pending' || change.status === 'modified') && (
                                            <button 
                                                className="action-btn process-btn"
                                                onClick={() => handleMarkAsProcessed(change.id)}
                                                title="Marker som behandlet"
                                            >
                                                <i className="fas fa-check"></i>
                                            </button>
                                        )}
                                        {canEditChanges && (
                                            <button 
                                                className="action-btn delete-btn"
                                                onClick={() => {
                                                    handleDelete(change.id);
                                                }}
                                                title="Slet"
                                                disabled={deletingId === change.id}
                                            >
                                                {deletingId === change.id ? (
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                ) : (
                                                    <i className="fas fa-trash"></i>
                                                )}
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default PayrollChanges; 